import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@tanstack/react-router';
import { parseApiError, isNotFoundError } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { Sentry } from '../lib/sentry';

interface ErrorFallbackProps {
  error: Error;
  reset: () => void;
}

export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const notFound = isNotFoundError(error);

  // Not-found is an expected navigation state, not a bug — never reported.
  // Runs once per distinct `error` instance rather than in the render body,
  // which would double-report on every re-render.
  useEffect(() => {
    if (notFound) return;
    Sentry.captureException(error, { tags: { source: 'router-error-boundary' } });
  }, [error, notFound]);

  if (notFound) {
    return (
      <div
        data-testid="error-fallback"
        className="flex flex-col items-center justify-center h-screen gap-4"
      >
        <p>{t('error.notFound')}</p>
        <Button
          data-testid="go-back-button"
          variant="outline"
          onClick={() => router.history.back()}
        >
          {t('error.goBack')}
        </Button>
      </div>
    );
  }

  const parsed = parseApiError(error);
  return (
    <div
      data-testid="error-fallback"
      className="flex flex-col items-center justify-center h-screen gap-4"
    >
      <p>{t('error.generic')}</p>
      <p>{parsed.message}</p>
      <Button
        data-testid="retry-button"
        variant="outline"
        onClick={() => {
          void router.invalidate();
          reset();
        }}
      >
        {t('error.retry')}
      </Button>
    </div>
  );
}
